<template>
  <section class="standard-cta" :class="{ 'standard-cta--light': light }">
    <v-container class="standard-cta__inner">
      <h2 class="font-sora standard-cta__title">{{ heading }}</h2>
      <p class="standard-cta__lead">{{ body }}</p>
      <div class="standard-cta__grid">
        <component
          :is="watchIsExternal ? 'a' : 'router-link'"
          v-bind="watchBind"
          class="standard-cta__card"
        >
          <strong>▶ {{ CTA.watchDemo.label }}</strong>
          <span>{{ CTA.watchDemo.description }}</span>
        </component>
        <component
          :is="experienceIsExternal ? 'a' : 'router-link'"
          v-bind="experienceBind"
          class="standard-cta__card"
          :rel="experienceIsExternal ? 'noopener' : undefined"
          :target="experienceIsExternal ? '_blank' : undefined"
        >
          <strong>▶ {{ CTA.experience.label }}</strong>
          <span>{{ CTA.experience.description }}</span>
        </component>
        <component
          :is="startedIsExternal ? 'a' : 'router-link'"
          v-bind="startedBind"
          class="standard-cta__card"
        >
          <strong>▶ {{ CTA.getStarted.label }}</strong>
          <span>{{ CTA.getStarted.description }}</span>
        </component>
      </div>
    </v-container>
  </section>
</template>

<script>
import { CTA, STANDARD_CTA, getExperienceHref } from '@/config/ctas';

export default {
  name: 'StandardCta',
  props: {
    title: { type: String, default: '' },
    lead: { type: String, default: '' },
    light: { type: Boolean, default: false },
    /** Hide Get Started (e.g. on Partners-only pages) — not used by default */
    hideGetStarted: { type: Boolean, default: false },
  },
  data() {
    return { CTA };
  },
  computed: {
    heading() {
      return this.title || STANDARD_CTA.eyebrow;
    },
    body() {
      return this.lead || STANDARD_CTA.lead;
    },
    experienceHref() {
      return getExperienceHref();
    },
    experienceIsExternal() {
      return /^https?:\/\//.test(this.experienceHref);
    },
    experienceBind() {
      return this.experienceIsExternal
        ? { href: this.experienceHref }
        : { to: this.experienceHref };
    },
    watchIsExternal() {
      return /^https?:\/\//.test(CTA.watchDemo.href);
    },
    startedIsExternal() {
      return /^https?:\/\//.test(CTA.getStarted.href);
    },
    watchBind() {
      return this.watchIsExternal
        ? { href: CTA.watchDemo.href }
        : { to: CTA.watchDemo.href };
    },
    startedBind() {
      return this.startedIsExternal
        ? { href: CTA.getStarted.href }
        : { to: CTA.getStarted.href };
    },
  },
};
</script>

<style scoped>
.standard-cta {
  background: linear-gradient(135deg, #002458 0%, #0b3a74 100%);
  color: #fff;
  padding: 72px 0;
  text-align: center;
}

.standard-cta--light {
  background: #f7f9fb;
  color: var(--brand-navy, #002458);
}

.standard-cta__inner {
  max-width: 980px;
}

.standard-cta__title {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 12px;
  color: inherit;
}

.standard-cta--light .standard-cta__title {
  color: var(--brand-navy, #002458);
}

.standard-cta__lead {
  max-width: 640px;
  margin: 0 auto 32px;
  font-size: 17px;
  line-height: 1.5;
  color: #c9d7ee;
}

.standard-cta--light .standard-cta__lead {
  color: var(--brand-grey, #6b7280);
}

.standard-cta__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.standard-cta__card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 22px 18px;
  text-decoration: none;
  color: #fff;
  transition: background 0.15s ease;
}

.standard-cta--light .standard-cta__card {
  background: #fff;
  border: 1px solid #e6ebf1;
  color: var(--brand-navy, #002458);
  box-shadow: 0 8px 24px rgba(0, 36, 88, 0.06);
}

.standard-cta__card:hover {
  background: rgba(8, 160, 168, 0.35);
  color: #fff;
}

.standard-cta--light .standard-cta__card:hover {
  border-color: var(--brand-teal, #08a0a8);
  color: var(--brand-navy, #002458);
}

.standard-cta__card strong {
  font-size: 17px;
}

.standard-cta__card span {
  font-size: 14px;
  line-height: 1.45;
  color: #b8c9e4;
}

.standard-cta--light .standard-cta__card span {
  color: var(--brand-grey, #6b7280);
}

@media screen and (max-width: 900px) {
  .standard-cta__grid {
    grid-template-columns: 1fr;
  }

  .standard-cta {
    padding: 56px 0;
  }

  .standard-cta__title {
    font-size: 26px;
  }
}
</style>
